import { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { NetworkState } from '@/types/game';
import { SentimentData, DEFAULT_SENTIMENT, classifySentiment, SENTIMENT_LABELS } from '@/lib/types/sentiment';

const MEMPOOL_API = 'https://mempool.space/api';
const MEMPOOL_WS = 'wss://mempool.space/api/v1/ws';
const COINGECKO_API = 'https://api.coingecko.com/api/v3';

const INITIAL_NETWORK: NetworkState = {
  btcPrice: 0,
  priceChange24h: 0,
  mempoolCongestion: 0,
  pendingTxCount: 0,
  mempoolVsize: 0,
  recommendedFees: { low: 1, medium: 2, high: 3 },
  lastBlockHeight: 0,
  lastBlockTime: Date.now(),
  timeSinceLastBlock: 0,
  difficultyProgress: 0,
  difficultyChange: 0,
  remainingBlocks: 2016,
  hashrateTHs: 0,
  previousHashrateTHs: 0,
  blocksToHalving: 0,
  daysToHalving: 0,
  hashrateChange7d: 0,
  lightningCapacitySats: 0,
  lightningChannels: 0,
  lightningNodes: 0,
  blocksTodayCount: 0,
  avgBlockTimeSec: 600,
};

const MAX_MEMPOOL_VSIZE = 300_000_000;

async function fetchJSON<T>(url: string): Promise<T | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

interface MempoolStats { count: number; vsize: number; total_fee: number; }
interface FeeEstimates { fastestFee: number; halfHourFee: number; hourFee: number; economyFee: number; minimumFee: number; }
interface BlockSummary { id: string; height: number; timestamp: number; }
interface CoinGeckoPrice { bitcoin: { usd: number; usd_24h_change: number; } }
interface DifficultyAdjustment {
  progressPercent: number;
  difficultyChange: number;
  remainingBlocks: number;
  estimatedRetargetDate: number;
}
interface HashrateResponse {
  currentHashrate: number;
  currentDifficulty: number;
  hashrates: { timestamp: number; avgHashrate: number }[];
}
interface LightningStats {
  latest: {
    channel_count: number;
    total_capacity: number;
    node_count: number;
  };
}

export function useBitcoinData() {
  const [network, setNetwork] = useState<NetworkState>(INITIAL_NETWORK);
  const [blockJustFound, setBlockJustFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(0);
  const [sentiment, setSentiment] = useState<SentimentData>(DEFAULT_SENTIMENT);
  const prevBlockHeightRef = useRef<number>(0);
  const blocksTodayRef = useRef<number>(0);
  const priceRef = useRef<{ price: number; change: number }>({ price: 0, change: 0 });
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const blockJustFoundTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const miningRef = useRef({ difficultyProgress: 0, difficultyChange: 0, remainingBlocks: 2016, hashrateTHs: 0, previousHashrateTHs: 0, hashrateChange7d: 0 });
  const lightningRef = useRef({ lightningCapacitySats: 0, lightningChannels: 0, lightningNodes: 0 });

  // --- Fear & Greed Index fetching ---
  const fetchFearGreed = useCallback(async () => {
    try {
      const data = await fetchJSON<{ data: { value: string; value_classification: string; timestamp: string }[] }>(
        'https://api.alternative.me/fng/?limit=1'
      );
      if (data?.data?.[0]) {
        const raw = data.data[0];
        const value = parseInt(raw.value, 10);
        const bucket = classifySentiment(value);
        setSentiment({
          value,
          bucket,
          label: SENTIMENT_LABELS[bucket],
          timestamp: parseInt(raw.timestamp, 10) * 1000,
        });
      }
    } catch {
      // Silently fail — sentiment is optional
    }
  }, []);

  // --- Mining data fetching ---
  const fetchMiningData = useCallback(async () => {
    const [diffAdj, hashrate] = await Promise.all([
      fetchJSON<DifficultyAdjustment>(`${MEMPOOL_API}/v1/difficulty-adjustment`),
      fetchJSON<HashrateResponse>(`${MEMPOOL_API}/v1/mining/hashrate/3d`),
    ]);
    if (diffAdj) {
      miningRef.current.difficultyProgress = diffAdj.progressPercent ?? 0;
      miningRef.current.difficultyChange = diffAdj.difficultyChange ?? 0;
      miningRef.current.remainingBlocks = diffAdj.remainingBlocks ?? 2016;
    }
    if (hashrate) {
      const currentTHs = hashrate.currentHashrate / 1e12;
      miningRef.current.hashrateTHs = currentTHs;
      const prev = hashrate.hashrates?.[0];
      const prevTHs = prev ? prev.avgHashrate / 1e12 : currentTHs;
      miningRef.current.previousHashrateTHs = prevTHs;
      miningRef.current.hashrateChange7d = prevTHs > 0
        ? ((currentTHs - prevTHs) / prevTHs) * 100 : 0;
    }
  }, []);

  // --- Price fetching (CoinGecko with mempool.space fallback) ---
  const fetchPrice = useCallback(async () => {
    const data = await fetchJSON<CoinGeckoPrice>(
      `${COINGECKO_API}/simple/price?ids=bitcoin&vs_currencies=usd&include_24hr_change=true`
    );
    if (data?.bitcoin) {
      priceRef.current = { price: data.bitcoin.usd, change: data.bitcoin.usd_24h_change ?? 0 };
    } else {
      const mp = await fetchJSON<{ USD: number }>(`${MEMPOOL_API}/v1/prices`);
      if (mp?.USD) priceRef.current = { price: mp.USD, change: 0 };
    }
  }, []);

  // --- Lightning Network stats (slow cadence) ---
  const fetchLightning = useCallback(async () => {
    const data = await fetchJSON<LightningStats>(`${MEMPOOL_API}/v1/lightning/statistics/latest`);
    if (data?.latest) {
      lightningRef.current = {
        lightningCapacitySats: data.latest.total_capacity ?? 0,
        lightningChannels: data.latest.channel_count ?? 0,
        lightningNodes: data.latest.node_count ?? 0,
      };
    }
  }, []);

  // --- Initial REST fetch for bootstrap ---
  const fetchInitialData = useCallback(async () => {
    const [mempoolStats, fees, blocks] = await Promise.all([
      fetchJSON<MempoolStats>(`${MEMPOOL_API}/mempool`),
      fetchJSON<FeeEstimates>(`${MEMPOOL_API}/v1/fees/recommended`),
      fetchJSON<BlockSummary[]>(`${MEMPOOL_API}/v1/blocks`),
    ]);
    await Promise.all([fetchPrice(), fetchMiningData(), fetchFearGreed(), fetchLightning()]);

    if (!mempoolStats || !fees || !blocks?.length) { setLoading(false); setError(true); return; }

    const latestBlock = blocks[0];
    prevBlockHeightRef.current = latestBlock.height;
    const congestion = Math.min(100, Math.round((mempoolStats.vsize / MAX_MEMPOOL_VSIZE) * 100));
    const timeSinceLastBlock = Math.floor((Date.now() / 1000) - latestBlock.timestamp);

    const nextHalvingBlock = Math.ceil(latestBlock.height / 210000) * 210000;
    const blocksToHalving = nextHalvingBlock - latestBlock.height;
    const daysToHalving = (blocksToHalving * 10) / 60 / 24;

    // Calculate blocks today: estimate from elapsed hours since midnight
    // (REST API only returns ~15 blocks, so counting them directly is inaccurate)
    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const todayStartSec = Math.floor(todayStart.getTime() / 1000);
    const hoursSinceMidnight = (Date.now() / 1000 - todayStartSec) / 3600;
    const blocksTodayCount = Math.round(hoursSinceMidnight * 6); // ~6 blocks/hour
    blocksTodayRef.current = blocksTodayCount;
    // Avg interval from recent blocks (up to 15 blocks)
    let avgBlockTimeSec = 600;
    if (blocks.length >= 2) {
      const intervals: number[] = [];
      for (let i = 0; i < Math.min(blocks.length - 1, 15); i++) {
        intervals.push(Math.abs(blocks[i].timestamp - blocks[i + 1].timestamp));
      }
      avgBlockTimeSec = Math.round(intervals.reduce((a, b) => a + b, 0) / intervals.length);
    }

    setNetwork({
      btcPrice: Math.round(priceRef.current.price * 100) / 100,
      priceChange24h: Math.round(priceRef.current.change * 100) / 100,
      mempoolCongestion: congestion,
      pendingTxCount: mempoolStats.count,
      mempoolVsize: mempoolStats.vsize,
      recommendedFees: { low: fees.economyFee, medium: fees.halfHourFee, high: fees.fastestFee },
      lastBlockHeight: latestBlock.height,
      lastBlockTime: latestBlock.timestamp * 1000,
      timeSinceLastBlock: Math.max(0, timeSinceLastBlock),
      ...miningRef.current,
      blocksToHalving,
      daysToHalving,
      ...lightningRef.current,
      blocksTodayCount,
      avgBlockTimeSec,
    });
    setLoading(false);
    setError(false);
    setLastUpdated(Date.now());
  }, [fetchPrice, fetchMiningData, fetchFearGreed, fetchLightning]);

  // --- WebSocket connection ---
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(MEMPOOL_WS);
      wsRef.current = ws;

    ws.onopen = () => {
      // Subscribe to blocks, mempool stats, and live fees
      ws.send(JSON.stringify({ action: 'want', data: ['blocks', 'stats', 'mempool-blocks'] }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        let hasNewBlock = false;

        // Build a single update object to avoid multiple setState calls
        const update: Partial<NetworkState> = {};

        // New block
        if (msg.block) {
          const block = msg.block;
          if (prevBlockHeightRef.current > 0 && block.height > prevBlockHeightRef.current) {
            hasNewBlock = true;
          }
          prevBlockHeightRef.current = block.height;
          update.lastBlockHeight = block.height;
          update.lastBlockTime = block.timestamp * 1000;
          update.timeSinceLastBlock = 0;
          // Recalculate halving countdown
          const nextH = Math.ceil(block.height / 210000) * 210000;
          update.blocksToHalving = nextH - block.height;
          update.daysToHalving = (update.blocksToHalving * 10) / 60 / 24;
          // Increment blocks today on new block
          if (hasNewBlock) {
            blocksTodayRef.current += 1;
            update.blocksTodayCount = blocksTodayRef.current;
          }
        }

        // Mempool info (stats update)
        // WS sends bitcoind getmempoolinfo format: size=txCount, bytes=totalBytes, vsize=totalVsize
        // REST /api/mempool uses: count, vsize — field names differ!
        if (msg.mempoolInfo) {
          const info = msg.mempoolInfo;
          const txCount = info.count ?? info.size ?? 0;
          const vsizeBytes = info.vsize ?? info.bytes ?? 0;
          if (txCount > 0 || vsizeBytes > 0) {
            update.mempoolCongestion = Math.min(100, Math.round((vsizeBytes / MAX_MEMPOOL_VSIZE) * 100));
            update.pendingTxCount = txCount;
            update.mempoolVsize = vsizeBytes;
          }
        }

        // Fee estimates
        if (msg.fees) {
          const f = msg.fees;
          update.recommendedFees = {
            low: f.economyFee,
            medium: f.halfHourFee,
            high: f.fastestFee,
          };
        }

        // Apply all updates in a single batched call
        if (Object.keys(update).length > 0 || hasNewBlock) {
          // Use queueMicrotask to ensure we're in a clean execution context
          queueMicrotask(() => {
            if (hasNewBlock) {
              setBlockJustFound(true);
              clearTimeout(blockJustFoundTimerRef.current);
              blockJustFoundTimerRef.current = setTimeout(() => setBlockJustFound(false), 4000);
            }
            if (Object.keys(update).length > 0) {
              setNetwork(prev => ({ ...prev, ...update }));
              setLastUpdated(Date.now());
            }
          });
        }
      } catch (err) {
        console.warn('WS message parse error:', err);
      }
    };

    ws.onclose = () => {
      // Reconnect after 5 seconds
      reconnectTimerRef.current = setTimeout(connectWebSocket, 5000);
    };

    ws.onerror = () => {
      ws.close();
    };
    } catch {
      // WebSocket constructor failed (e.g. DNS not resolved) — retry silently
      reconnectTimerRef.current = setTimeout(connectWebSocket, 5000);
    }
  }, []);

  // --- Bootstrap: REST fetch then WebSocket ---
  useEffect(() => {
    fetchInitialData().then(connectWebSocket);

    return () => {
      wsRef.current?.close();
      clearTimeout(reconnectTimerRef.current);
      clearTimeout(blockJustFoundTimerRef.current);
    };
  }, [fetchInitialData, connectWebSocket]);

  // --- Price polling (every 30s, separate from WS) ---
  useEffect(() => {
    const interval = setInterval(async () => {
      await fetchPrice();
      setNetwork(prev => ({
        ...prev,
        btcPrice: Math.round(priceRef.current.price * 100) / 100,
        priceChange24h: Math.round(priceRef.current.change * 100) / 100,
      }));
    }, 30_000);
    return () => clearInterval(interval);
  }, [fetchPrice]);

  // --- Mining data polling (every 5 min) ---
  useEffect(() => {
    const interval = setInterval(async () => {
      await fetchMiningData();
      setNetwork(prev => ({ ...prev, ...miningRef.current }));
    }, 300_000);
    return () => clearInterval(interval);
  }, [fetchMiningData]);

  // --- Lightning Network polling (every 5 min) ---
  useEffect(() => {
    const interval = setInterval(async () => {
      await fetchLightning();
      setNetwork(prev => ({ ...prev, ...lightningRef.current }));
    }, 300_000);
    return () => clearInterval(interval);
  }, [fetchLightning]);

  // --- timeSinceLastBlock ticker (every second) ---
  useEffect(() => {
    const interval = setInterval(() => {
      setNetwork(prev => {
        if (prev.lastBlockTime === 0) return prev;
        const timeSince = Math.floor((Date.now() - prev.lastBlockTime) / 1000);
        return { ...prev, timeSinceLastBlock: Math.max(0, timeSince) };
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // --- Fear & Greed polling (every 10 minutes — it updates daily) ---
  useEffect(() => {
    const interval = setInterval(fetchFearGreed, 600_000);
    return () => clearInterval(interval);
  }, [fetchFearGreed]);

  return { network, blockJustFound, loading, error, lastUpdated, sentiment };
}
